import { SVGProps, useMemo } from 'react';
import chroma from 'chroma-js';
import { BaseContainerComponentPropsType } from '../../util/types';
import { isWarmColor } from '../../util/isWarmColor';
import AvatarArtwork, { type RenderShadowFn } from '../AvatarArtwork';
import { avatarDefaultBackgroundColors } from '../../config/avatarColors';

const renderShadowWarm: RenderShadowFn = (props) => (
  <polygon
    {...props}
    points="48.01 22.99 47.41 47.97 31.58 47.97 18.94 36.07 41.65 17.51 48.01 22.99"
  />
);

const renderShadowCool: RenderShadowFn = (props) => (
  <polygon
    {...props}
    points="47.93 25.43 47.97 47.97 34.06 47.97 20.72 38.18 32.32 14.08 47.93 25.43"
  />
);

const DEFAULT_BG = avatarDefaultBackgroundColors.smokeStack;

export let SmokeStackAvatarArtwork = (
  props: {
    backgroundColor?: string;
  } & BaseContainerComponentPropsType &
    SVGProps<SVGSVGElement>,
) => {
  const bg = props.backgroundColor || DEFAULT_BG;
  const isWarm = isWarmColor(bg);
  const hatFill = useMemo(() => chroma(bg).darken(1).hex(), [bg]);
  const hatStroke = useMemo(() => chroma(bg).darken(1.8).hex(), [bg]);

  return (
    <AvatarArtwork
      {...props}
      defaultBackgroundColor={DEFAULT_BG}
      renderShadow={isWarm ? renderShadowWarm : renderShadowCool}
    >
      {isWarm ? (
        <>
          <path
            d="M32.45,16.12l-17.85,1.62.46-3.55c.2-2.32,1.91-4.33,4.39-5.23.3-.11.64-.08.93.04.57.24,1.6.59,2.64.53.7-.04,1.37-.44,1.95-.81.44-.28,1.04-.32,1.52-.08,1.47.72,4.53,2.41,5.07,4.2l.89,3.28Z"
            style={{
              fill: hatFill,
              stroke: hatStroke,
              strokeMiterlimit: 10,
              strokeWidth: 0.75,
            }}
          />
          <path
            d="M18.2,11.83c.04-.5.73-1.6,1.71-1.5,0,1.44-1.46,2.84-2.95,3.54,0,0,1.17-1.08,1.25-2.05Z"
            style={{
              fill: hatStroke,
              stroke: hatStroke,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
              strokeWidth: 0.75,
            }}
          />
          <path
            d="M27.75,11.29c-.17-.48-1.09-1.43-1.93-1.16.38,1.4,2.07,2.51,3.6,2.93,0,0-1.34-.84-1.67-1.77Z"
            style={{
              fill: hatStroke,
              stroke: hatStroke,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
              strokeWidth: 0.75,
            }}
          />
          <path
            d="M23.22,15.06c2.27-.13,9.09.33,9.09.33l.37,2.12-18.09.7.18-1.86s6.32-1.17,8.45-1.29Z"
            style={{ fill: hatStroke }}
          />
          <path
            d="M42.13,18.31c.28,2.35-7.38,5.34-17.39,6.14-10.01.8-18.43-.89-18.87-3.23-.36-1.9,5.94-3.76,17.7-4.73,13.53-1.12,18.35.03,18.56,1.82Z"
            style={{
              fill: hatFill,
              stroke: hatStroke,
              strokeMiterlimit: 10,
              strokeWidth: 0.75,
            }}
          />
          <path
            d="M31.3,33.32l-3.8,4.2c-.74.72-1.8,1.14-2.91,1.14h-1.9c-1.11,0-2.16-.41-2.91-1.14l-3.79-4.2c-.76-.71-1.18-1.64-1.16-2.6l-.23-10.15c0-.19,4.52-2.19,9.05-2.19s9.04,1.11,9.05,1.3l-.23,11.03c.01.96-.4,1.89-1.17,2.61Z"
            style={{ fill: '#753602' }}
          />
          <path
            d="M27.89,37.06c0,1.35-1.75,2.58-3.91,2.58s-3.91-1.23-3.91-2.58,1.75-.55,3.91-.55,3.91-.79,3.91.55Z"
            style={{ fill: '#120901' }}
          />
          <ellipse
            cx="23.99"
            cy="35.91"
            rx="1.16"
            ry=".37"
            style={{ fill: '#120901' }}
          />
          <path
            d="M21.07,32.96c.39-.37,1.74-.96,3.07-.95,1.85.01,3.64.8,2.61,1.54-.7.5-7.66,1.08-5.67-.58Z"
            style={{ fill: '#291301' }}
          />
          <path
            d="M14.49,21.88c4.06-1.66,9.47-4.94,13.58-3.68,5.11,1.56,5.34,1.04,4.38,6.29-.19,1.05-.47-3.6-1.34-4.37-4.58-.87-9.43-1.85-13.76.2-1.42.52-1.65,7.91-2.21,2.82l-.65-1.25Z"
            style={{ fill: '#120901' }}
          />
          <path
            d="M24.4,18.48c-12.43.95-10.93,4.27-9.77,5.25-.02-1.47,4.24-3.3,9.82-3.62,3.94-.23,6.95.83,8.68,1.6.84-.16.4-1.56,1.24-1.73,1.9-.39,3.2-2.53-9.98-1.51Z"
            style={{ fill: '#1c0605' }}
          />
          <path
            d="M23.37,17.58c-5.6.35-9.92,1.46-10.24,2.46-.46,1.46-.33,2.87.66,2.96-.02-1.15,5.2-3.35,10.65-3.6,3.85-.18,7.87.96,8.58,2.59.82-.12,4.28-3.31,2.2-4.6-1.6-.99,1.01-.61-11.86.2Z"
            style={{ fill: hatFill }}
          />
          <path
            d="M15.12,24.16c.19-.77.04-1.66-.36-2.15-.26-.32-.66-.45-.94-.12-.24.28-.3.77-.33,1.21-.08,1.45.05,2.91.38,4.23.09.35.27.74.51.63.09-.04.17-.16.23-.27.38-.69.59-1.59.57-2.46"
            style={{ fill: '#753602' }}
          />
          <path
            d="M32.49,24.1c-.13-.78.09-1.66.54-2.12.29-.29.71-.4.97-.05.22.29.25.79.24,1.23-.03,1.45-.28,2.89-.72,4.19-.11.34-.33.72-.57.59-.09-.05-.16-.17-.21-.29-.34-.72-.48-1.64-.39-2.5"
            style={{ fill: '#753602' }}
          />
          <path
            d="M26.77,33.42s-1.41,1.27-2.85,1.21c-2.03-.1-2.65-.68-2.65-.68l-.42-.28,2.11-.08c.63-.08,1.82-.34,2.4-.28.33.03,1.4.12,1.4.12Z"
            style={{ fill: '#fff' }}
          />
          <path
            d="M22.49,35.08c-1.03-.22-1.8-1.6-1.63-1.51,2.31,1.19,4.98.82,5.91-.15-.57,2.45-3.55,1.82-4.28,1.66Z"
            style={{ fill: '#3d1c01' }}
          />
          <path
            d="M28.96,33.72c-.04.13-.16.27-.34.27-.08,0-.14-.03-.2-.05-.92-.33-1.79-.72-2.57-1.17-.18-.1-.37-.21-.6-.26-.26-.05-.55-.02-.81-.09-.09-.03-.18-.06-.25-.11-.04,0-.07,0-.11,0-.04,0-.09,0-.13.03-.04,0-.07.02-.09.03-.16.04-.33-.03-.49.02h-.01c-.07.02-.12.06-.18.05-.2.03-.42.02-.62.06-.21.05-.39.17-.56.26-.75.45-1.55.84-2.42,1.17-.05.02-.12.05-.18.05-.17,0-.28-.15-.32-.27-.14-.43-.09-.89.07-1.31.07-.18.16-.36.32-.52.18-.18.45-.31.71-.43.55-.26,1.2-.76,1.86-.81.41-.04.81,0,1.21-.05.18-.02.58.13.7.13s.55-.15.75-.13c.42.05.85,0,1.29.05.70.06,1.24.55,1.83.81.28.12.56.24.76.43.17.16.26.33.33.52.17.43.24.89.08,1.31Z"
            style={{ fill: '#120901' }}
          />
        </>
      ) : (
        <>
          <path
            d="M12.4,19.67c.14,1.38.93,2.65,2.02,3.43.24.17.49.32.77.39.24.06.49.07.74.07,2.68.03,5.36.07,8.04.1,1.29.02,2.57.03,3.86-.04,1.42-.09,2.84-.28,4.24-.6.37-.08.75-.18,1.06-.4.3-.21.51-.53.69-.85.35-.6.64-1.26.71-1.96s-.08-1.45-.52-1.98c-.6-.72-1.58-.91-2.49-1-1.68-.17-3.38-.12-5.07-.07-1.68.05-3.36.09-5.03.14"
            style={{
              fill: hatStroke,
              stroke: hatStroke,
              strokeMiterlimit: 10,
              strokeWidth: 0.75,
            }}
          />
          <path
            d="M31.3,33.32l-3.8,4.2c-.74.72-1.8,1.14-2.91,1.14h-1.9c-1.11,0-2.16-.41-2.91-1.14l-3.79-4.2c-.76-.71-1.18-1.64-1.16-2.6l-.23-10.15c0-.19,4.52-2.19,9.05-2.19s9.04,1.11,9.05,1.3l-.23,11.03c.01.96-.4,1.89-1.17,2.61Z"
            style={{ fill: '#753602' }}
          />
          <path
            d="M27.89,37.06c0,1.35-1.75,2.58-3.91,2.58s-3.91-1.23-3.91-2.58,1.75-.55,3.91-.55,3.91-.79,3.91.55Z"
            style={{ fill: '#120901' }}
          />
          <ellipse
            cx="23.99"
            cy="35.91"
            rx="1.16"
            ry=".37"
            style={{ fill: '#120901' }}
          />
          <path
            d="M21.2,33.31c1.05-.52,6.73-1.17,5.59.69-.86.03-7.59.97-5.59-.69Z"
            style={{ fill: '#291301' }}
          />
          <path
            d="M28.96,34.19c-.04.13-.16.27-.34.27-.08,0-.14-.03-.2-.05-.92-.33-1.79-.72-2.57-1.17-.18-.1-.37-.21-.6-.26-.26-.05-.55-.02-.81-.09-.09-.03-.18-.06-.25-.11-.04,0-.07,0-.11,0-.04,0-.09,0-.13.03-.04,0-.07.02-.09.03-.16.04-.33-.03-.49.02h-.01c-.07.02-.12.06-.18.05-.2.03-.42.02-.62.06-.21.05-.39.17-.56.26-.75.45-1.55.84-2.42,1.17-.05.02-.12.05-.18.05-.17,0-.28-.15-.32-.27-.14-.43-.09-.89.07-1.31.07-.18.16-.36.32-.52.18-.18.45-.31.71-.43.55-.26,1.2-.76,1.86-.81.41-.04.81,0,1.21-.05.18-.02.58.13.7.13s.55-.15.75-.13c.42.05.85,0,1.29.05.70.06,1.24.55,1.83.81.28.12.56.24.76.43.17.16.26.33.33.52.17.43.24.89.08,1.31Z"
            style={{ fill: '#120901' }}
          />
          <path
            d="M22.49,34.91c-1.03-.22-1.69-.75-1.55-.87.33-.29,5.28-.44,5.85-.03-.85,1.11-3.57,1.06-4.3.91Z"
            style={{ fill: '#3d1c01' }}
          />
          <path
            d="M14.6,19.21c4.06-1.66,9.37-2.26,13.48-1.01,5.11,1.56,5.66,1.76,3.85,6.78-.4,1.12-.18-4.42-.81-4.86-3.8-2.71-9.43-1.85-13.76.2-1.42.52-1.19,7.62-2.69,2.56l-.07-3.67Z"
            style={{ fill: '#120901' }}
          />
          <path
            d="M15.12,24.16c.19-.77.04-1.66-.36-2.15-.26-.32-.66-.45-.94-.12-.24.28-.3.77-.33,1.21-.08,1.45.05,2.91.38,4.23.09.35.27.74.51.63.09-.04.17-.16.23-.27.38-.69.59-1.59.57-2.46"
            style={{ fill: '#753602' }}
          />
          <path
            d="M32.49,24.1c-.13-.78.09-1.66.54-2.12.29-.29.71-.4.97-.05.22.29.25.79.24,1.23-.03,1.45-.28,2.89-.72,4.19-.11.34-.33.72-.57.59-.09-.05-.16-.17-.21-.29-.34-.72-.48-1.64-.39-2.5"
            style={{ fill: '#753602' }}
          />
          <path
            d="M15.01,21.05c-1.41.39-2.62-.98-2.99-1.8-.98-2.68,5.35-8.71,11.99-8.71s11.74,6.16,11.74,8.49c0,1.23-1.58,3.19-3.33,2.21"
            style={{
              fill: hatFill,
              stroke: hatStroke,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
              strokeWidth: 0.75,
            }}
          />
          <ellipse
            cx="24.25"
            cy="10.55"
            rx="1.75"
            ry=".82"
            style={{
              fill: hatFill,
              stroke: hatStroke,
              strokeMiterlimit: 10,
              strokeWidth: 0.75,
            }}
          />
          <path
            d="M15.72,21.33c1.31.6,14.33,1.15,16.21,3.65.78-1.69.27-3.4.27-3.4l-.29-1.39h-15.11s-1.08,1.14-1.08,1.14Z"
            style={{ fill: '#000', mixBlendMode: 'multiply', opacity: 0.31 }}
          />
          <path
            d="M22.22,11.98s-5.54,2.54-7.02,5.69"
            style={{
              fill: 'none',
              stroke: hatStroke,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
              strokeWidth: 0.75,
            }}
          />
          <path
            d="M22.08,17.33c.07-.15-.16-2.11,1.56-4.77"
            style={{
              fill: 'none',
              stroke: hatStroke,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
              strokeWidth: 0.75,
            }}
          />
          <path
            d="M27.11,12.19c.48.25,3.9,2.46,5.2,4.92"
            style={{
              fill: 'none',
              stroke: hatStroke,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
              strokeWidth: 0.75,
            }}
          />
          <path
            d="M25.34,12.54c.4.38.7.86.85,1.38"
            style={{
              fill: 'none',
              stroke: hatStroke,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
              strokeWidth: 0.75,
            }}
          />
          <ellipse
            cx="24.1"
            cy="21.06"
            rx="9.18"
            ry="1.02"
            style={{
              fill: hatFill,
              stroke: hatStroke,
              strokeMiterlimit: 10,
              strokeWidth: 0.75,
            }}
          />
        </>
      )}
    </AvatarArtwork>
  );
};
